import { create } from 'zustand'
import { devtools, persist } from 'zustand/middleware'
import * as LoginAPI from '../../../API/LoginAPI'
import { immer } from 'zustand/middleware/immer'
import Axios from 'src/Axios'
import { OAUTH_ENABLED } from 'src/Entities/AuthEntities'

// Constants for timing
export const SECOND = 1000
export const MINUTE = 60 * SECOND

export const ACCESS_TOKEN_VALIDITY = 15 * MINUTE
// Refresh BEFORE the access token expires so requests never go out with a stale token
export const REFRESH_INTERVAL = 13 * MINUTE
export const SESSION_TIMEOUT = 14 * MINUTE
export const MODAL_TIMEOUT = 2 * MINUTE

const initialState = {
    sendEmailWithOTPData: null,
    sendEmailWithOTPLoading: false,
    sendEmailWithOTPError: '',

    organizationsData: [],
    dataByOrganization: {},
    orgLoading: false,

    verifyEmailWithOTPData: null,
    verifyEmailWithOTPLoading: false,
    verifyEmailWithOTPError: '',

    sendInnviteFronOnboardingData: null,
    sendInnviteFronOnboardingLoading: false,
    sendInnviteFronOnboardingError: '',

    accessToken: null,
    refreshToken: null,
    isAuthenticated: false,
    inactivityTimeout: null,
    refreshInterval: null,
    isModalOpen: false,
    lastActivity: Date.now(),
    loading: false,
    ApiError: false
}

const LoginStore = (set, get) => ({
    ...initialState,
    setApiError: (bool) => {
        set({ ApiError: bool })
    },
    checkIsAuthenticated: (key, value) => {
        const storage = useLoginStore.getState().get(key)
        if (Array.isArray(storage)) {
            return Boolean(storage.find(ele => ele === value))
        } else {
            return storage === value
        }
    },
    get: (data) => {
        const value = localStorage.getItem(data)
        try {
            return JSON.parse(value)
        } catch (err) {
            return value
        }
    },
    set: (key, data) => {
        try {
            localStorage.setItem(key, data)
            return key + 'set Completed'
        } catch (err) {
            return 'Cannot set' + key + ':' + data
        }
    },
    clearDataByOrganization: () => {
        set({ dataByOrganization: {} })
    },
    sendEmailWithOTP: async (emailId) => {
        set({ sendEmailWithOTPLoading: true, sendEmailWithOTPError: '' })
        try {
            const response = await LoginAPI.sendEmailWithOTP(emailId)
            set({ sendEmailWithOTPLoading: false, sendEmailWithOTPData: response || {} })
            return response
        } catch (error) {
            set({
                sendEmailWithOTPError: error.message || 'An error occurred',
                sendEmailWithOTPLoading: false
            })
        }
    },

    verifyEmailWithOTP: async (emailId, otp) => {
        set({ verifyEmailWithOTPLoading: true, verifyEmailWithOTPError: '' })
        try {
            const response = await LoginAPI.verifyEmailWithOTP(emailId, otp)
            set({ verifyEmailWithOTPData: response || {}, verifyEmailWithOTPLoading: false })

            if (response?.status === 200) {
                if (OAUTH_ENABLED) await get().fetchAccessToken()
            }

            return response
        } catch (error) {
            set({
                verifyEmailWithOTPError: error.message || 'An error occurred',
                verifyEmailWithOTPLoading: false
            })
        }
    },

    getAllOrganizationByEmail: async (email) => {
        set({ organizationsData: [], orgLoading: true })
        try {
            const response = await LoginAPI.getAllOrganizationByEmail(email)
            set({ organizationsData: response.data, orgLoading: false })
        } catch (error) {
            set({ organizationsData: [], orgLoading: false })
        }
    },
    getDataByOrganization: async (email, orgId) => {
        set({ dataByOrganization: {}, orgLoading: true })
        try {
            const response = await LoginAPI.getDataByOrganization(email, orgId)
            set({ dataByOrganization: response.data, orgLoading: false })
        } catch (error) {
            set({ dataByOrganization: {}, orgLoading: false })
        }
    },
    sendInnviteFronOnboarding: async (orgId, emailId) => {
        set({ sendInnviteFronOnboardingLoading: true, sendInnviteFronOnboardingError: '' })
        try {
            const response = await LoginAPI.sendInnviteFronOnboarding(orgId, emailId)
            set({ sendInnviteFronOnboardingLoading: false, sendInnviteFronOnboardingData: response || {} })
            return response
        } catch (error) {
            set({
                sendInnviteFronOnboardingError: error.message || 'An error occurred', sendInnviteFronOnboardingLoading: false
            })
        }
    },

    fetchAccessToken: async () => {
        try {
            const response = await LoginAPI.getAccessToken()
            const data = response?.data

            if (data?.access_token) {
                set({
                    accessToken: data.access_token,
                    refreshToken: data.refresh_token,
                    isAuthenticated: true
                })

                Axios.setAccessToken(data.access_token)
                get().startInactivityTimer()
                get().startRefreshTokenTimer()
            } else {
                // LoginAPI.getAccessToken swallows HTTP errors ({ err }) - without this the
                // user continues into the app with no token and every API returns 401
                console.error('fetchAccessToken failed - no access_token in response', response?.err || response)
                set({ isAuthenticated: false, accessToken: null, refreshToken: null })
            }
        } catch (error) {
            set({ isAuthenticated: false, accessToken: null, refreshToken: null })
        }
    },

    refreshAccessToken: async () => {
        const { refreshToken } = get()
        console.log('Refreshing access token...')
        if (!refreshToken) {
            console.warn('No refresh token available')
            // Don't throw - just return null and let caller decide
            return null
        }
        try {
            const response = await LoginAPI.getRefreshToken(refreshToken)
            const data = response?.data

            if (data?.access_token) {
                const newAccessToken = data.access_token
                set({
                    accessToken: newAccessToken,
                    refreshToken: data.refresh_token || refreshToken,
                    isAuthenticated: true
                }, false, 'refreshAccessToken')

                Axios.setAccessToken(newAccessToken)
                get().startInactivityTimer()
                get().startRefreshTokenTimer()
                return newAccessToken
            } else {
                // No access token in response - refresh failed
                console.error('Refresh token response did not contain access_token')
                throw new Error('Token refresh failed')
            }
        } catch (error) {
            console.error('Error refreshing token:', error)
            set({ isAuthenticated: false, accessToken: null, refreshToken: null })
            throw error // Re-throw so interceptor can handle logout
        }
    },

    startInactivityTimer: () => {
        clearTimeout(get().inactivityTimeout)
        const timeout = setTimeout(() => {
            set({ isModalOpen: true })
            clearInterval(get().refreshInterval) // Stop refresh timer when idle.
        }, SESSION_TIMEOUT) // 14 minute idle = show modal

        set({ inactivityTimeout: timeout })
    },

    startRefreshTokenTimer: () => {
        clearInterval(get().refreshInterval)
        const interval = setInterval(async () => {
            if (!get().isModalOpen) {
                await get().refreshAccessToken()
            }
        }, REFRESH_INTERVAL) // 15 minute refresh interval
        set({ refreshInterval: interval })
    },

    resetInactivityTimer: () => {
        if (get().isModalOpen) return
        set({ lastActivity: Date.now() })
        get().startInactivityTimer()
        get().startRefreshTokenTimer()
    },

    staySignedIn: async () => {
        set({ loading: true })
        set({ isModalOpen: false })
        await get().refreshAccessToken()
        get().startInactivityTimer()
        get().startRefreshTokenTimer()
        set({ loading: false })
        setTimeout(() => {
            window.location.reload()
        }, 500);
    },

    logout: () => {
        clearTimeout(get().inactivityTimeout)
        clearInterval(get().refreshInterval)
        set({
            accessToken: null,
            refreshToken: null,
            isAuthenticated: false,
            isModalOpen: false
        })
        localStorage.clear()
        sessionStorage.clear()
        get().resetStore()
        localStorage.removeItem('auth-storage')
        window.location.href = '/'
    },

    resetStore: () => {
        set(initialState)
    }
})

export const useLoginStore = create(
    persist(devtools(immer(LoginStore)), {
        name: 'auth-storage',
        partialize: (state) => ({
            accessToken: state.accessToken,
            refreshToken: state.refreshToken,
            isAuthenticated: state.isAuthenticated,
            lastActivity: state.lastActivity,
            isModalOpen: state.isModalOpen
            // Explicitly exclude: loading, orgLoading, and other transient states
        }),
        onRehydrateStorage: (state) => (state?.isAuthenticated ? state : initialState)
    })
)

// Sync Axios with Zustand store (Fixed)
useLoginStore.subscribe((state) => {
    Axios.setAccessToken(state.accessToken)
})

// Setup Axios Interceptors after store creation
// Reinitialize interceptors with latest token
Axios.setupInterceptors(
    () => useLoginStore.getState().accessToken,
    useLoginStore.getState().refreshAccessToken,
    useLoginStore.getState().logout
)

// Component side
// add this to you component to reset idle timer.
// window.addEventListener('mousemove', resetInactivityTimer);
// window.addEventListener('keydown', resetInactivityTimer);
// window.addEventListener('scroll', resetInactivityTimer);
export const getAllOrganizationByEmail = (email) =>
    useLoginStore.getState().getAllOrganizationByEmail(email)
export const getDataByOrganization = (email, orgId) =>
    useLoginStore.getState().getDataByOrganization(email, orgId)
export const get = (value) =>
    useLoginStore.getState().get(value)
export const checkIsAuthenticated = (key, value) =>
    useLoginStore.getState().checkIsAuthenticated(key, value)
export const set = (key, value) =>
    useLoginStore.getState().set(key, value)
export const clearDataByOrganization = () =>
    useLoginStore.getState().clearDataByOrganization()
export const setApiError = (value) =>
    useLoginStore.getState().setApiError(value)
export const sendInnviteFronOnboarding = (orgId, emailid) =>
    useLoginStore.getState().sendInnviteFronOnboarding(orgId, emailid)
